// frontend/src/components/auth/PartnerRegisterForm.tsx
// ✅ UPDATED: Using new refactored hooks

import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { User, Mail, Lock, Eye, EyeOff, AlertCircle, Loader2, CheckCircle, Building, Shield, Phone } from 'lucide-react';
// import { useAuth } from '@/contexts/AuthContext'; // Not needed
import { useRegisterPartner } from '@/hooks/useAuth';
// import { prepareRegistrationData } from '@/utils/form'; // Not needed for basic registration

interface PartnerRegisterFormProps {
  className?: string;
}

const PartnerRegisterForm: React.FC<PartnerRegisterFormProps> = ({ className = '' }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Personal Information
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    password: '',
    password_confirm: '',
    
    // Business Information
    business_name: '',
    business_type: 'individual',
    experience_years: '',
    business_license: '',
    insurance_details: '',
    
    // Agreements
    terms_accepted: false,
    background_check_consent: false,
    marketing_consent: false
  });
  
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string | string[]>>({});
  const [isSuccess, setIsSuccess] = useState(false);

  // Auth context for state only
  // const { user } = useAuth(); // Not needed
  
  // New hook for partner registration
  const registerPartnerMutation = useRegisterPartner();
  
  const navigate = useNavigate();

  // Helper function to render errors consistently
  const renderFieldError = (fieldName: string) => {
    const error = errors[fieldName];
    if (!error) return null;

    return (
      <div className="mt-1 space-y-1">
        {Array.isArray(error) ? (
          error.map((err, index) => (
            <p key={index} className="text-sm text-red-600 flex items-start gap-1">
              <span className="text-red-500 mt-0.5">•</span>
              <span>{err}</span>
            </p>
          ))
        ) : (
          <p className="text-sm text-red-600 flex items-start gap-1">
            <span className="text-red-500 mt-0.5">•</span>
            <span>{error}</span>
          </p>
        )}
      </div>
    );
  };

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (step === 1) {
      if (!formData.first_name.trim()) newErrors.first_name = 'First name is required';
      if (!formData.last_name.trim()) newErrors.last_name = 'Last name is required';
      if (!formData.email.trim()) newErrors.email = 'Email is required';
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = 'Please enter a valid email address';
      }
      if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
      else if (!/^[+]?[\d\s\-\(\)]{10,}$/.test(formData.phone.replace(/\s/g, ''))) {
        newErrors.phone = 'Please enter a valid phone number';
      }
      if (!formData.password.trim()) newErrors.password = 'Password is required';
      else if (formData.password.length < 8) {
        newErrors.password = 'Password must be at least 8 characters long';
      }
      if (formData.password !== formData.password_confirm) {
        newErrors.password_confirm = 'Passwords do not match';
      }
    }

    if (step === 2) {
      if (!formData.business_name.trim()) newErrors.business_name = 'Business name is required';
      if (!formData.business_type) newErrors.business_type = 'Business type is required';
      if (!formData.terms_accepted) newErrors.terms_accepted = 'You must accept the terms and conditions';
      if (!formData.background_check_consent) newErrors.background_check_consent = 'Background check consent is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (currentStep < 2) {
      // Validate current step
      if (validateStep(currentStep)) {
        setCurrentStep(currentStep + 1);
      }
      return;
    }

    // Validate step 2 before submission
    if (!validateStep(2)) {
      return;
    }

    // Final submission
    setErrors({});

    try {
      // Prepare form data for partner registration
      const submitData = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
        password_confirm: formData.password_confirm,
        business_name: formData.business_name,
        business_type: formData.business_type,
        terms_accepted: formData.terms_accepted,
        background_check_consent: formData.background_check_consent,
        marketing_consent: formData.marketing_consent,
      };

      await registerPartnerMutation.mutateAsync(submitData);
      
      setIsSuccess(true);
      
      // Navigate to setup page after successful registration
      setTimeout(() => {
        navigate('/setup', { 
          state: { 
            message: 'Welcome! Please verify your email within 7 days and complete your service setup.',
            type: 'info'
          }
        });
      }, 3000);
      
    } catch (err: any) {
      console.error('Partner registration error:', err);
      
      // Handle API validation errors
      if (err.response?.data?.errors || err.errors || err.data?.errors) {
        const apiErrors = err.response?.data?.errors || err.errors || err.data?.errors;
        const newErrors: Record<string, string | string[]> = {};
        
        Object.entries(apiErrors).forEach(([field, messages]) => {
          newErrors[field] = Array.isArray(messages) ? messages : [messages as string];
        });
        
        setErrors(newErrors);
        
        // Go back to step 1 if there are personal info errors
        const personalFields = ['first_name', 'last_name', 'email', 'phone', 'password', 'password_confirm'];
        const hasPersonalErrors = Object.keys(newErrors).some(field => personalFields.includes(field));
        if (hasPersonalErrors) {
          setCurrentStep(1);
        }
      } else {
        // General error message
        const errorMessage = err.response?.data?.message || 
                           err.message || 
                           err.data?.message ||
                           'An unexpected error occurred. Please try again.';
        setErrors({ general: errorMessage });
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear errors when user starts typing
    if (errors[name] || errors.general) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
        general: ''
      }));
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const isLoading = registerPartnerMutation.isPending;

  // Success state
  if (isSuccess) {
    return (
      <div className={`min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8 ${className}`}>
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="mx-auto h-16 w-16 flex items-center justify-center rounded-full bg-green-100">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
              Partner Application Submitted!
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              Your application will be reviewed within 24-48 hours. You'll receive an email once approved.
            </p>
            <div className="mt-4 text-xs text-gray-500">
              Redirecting to setup page...
            </div>
          </div>
        </div>
      </div>
    );
  }

  const renderStep1 = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="first_name" className="block text-sm font-medium text-gray-700">
            First Name
          </label>
          <div className="mt-1 relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <User className="h-5 w-5 text-gray-400" />
            </div>
            <input
              id="first_name"
              name="first_name"
              type="text"
              autoComplete="given-name"
              required
              value={formData.first_name}
              onChange={handleInputChange}
              className={`block w-full pl-10 pr-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 sm:text-sm ${
                errors.first_name ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="First name"
            />
          </div>
          {renderFieldError('first_name')}
        </div>

        <div>
          <label htmlFor="last_name" className="block text-sm font-medium text-gray-700">
            Last Name
          </label>
          <div className="mt-1 relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <User className="h-5 w-5 text-gray-400" />
            </div>
            <input
              id="last_name"
              name="last_name"
              type="text"
              autoComplete="family-name"
              required
              value={formData.last_name}
              onChange={handleInputChange}
              className={`block w-full pl-10 pr-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 sm:text-sm ${
                errors.last_name ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Last name"
            />
          </div>
          {renderFieldError('last_name')}
        </div>
      </div>

      <div>
        <label htmlFor="email" className="block text-sm font-medium text-gray-700">
          Email Address
        </label>
        <div className="mt-1 relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Mail className="h-5 w-5 text-gray-400" />
          </div>
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            required
            value={formData.email}
            onChange={handleInputChange}
            className={`block w-full pl-10 pr-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 sm:text-sm ${
              errors.email ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="Enter your email address"
          />
        </div>
        {renderFieldError('email')}
      </div>

      <div>
        <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
          Phone Number
        </label>
        <div className="mt-1 relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Phone className="h-5 w-5 text-gray-400" />
          </div>
          <input
            id="phone"
            name="phone"
            type="tel"
            autoComplete="tel"
            required
            value={formData.phone}
            onChange={handleInputChange}
            className={`block w-full pl-10 pr-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 sm:text-sm ${
              errors.phone ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="Enter your phone number"
          />
        </div>
        {renderFieldError('phone')}
      </div>

      <div>
        <label htmlFor="password" className="block text-sm font-medium text-gray-700">
          Password
        </label>
        <div className="mt-1 relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Lock className="h-5 w-5 text-gray-400" />
          </div>
          <input
            id="password"
            name="password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            required
            value={formData.password}
            onChange={handleInputChange}
            className={`block w-full pl-10 pr-10 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 sm:text-sm ${
              errors.password ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="Create a strong password"
          />
          <button
            type="button"
            className="absolute inset-y-0 right-0 pr-3 flex items-center"
            onClick={() => setShowPassword(!showPassword)}
          >
            {showPassword ? (
              <EyeOff className="h-5 w-5 text-gray-400" />
            ) : (
              <Eye className="h-5 w-5 text-gray-400" />
            )}
          </button>
        </div>
        {renderFieldError('password')}
      </div>

      <div>
        <label htmlFor="password_confirm" className="block text-sm font-medium text-gray-700">
          Confirm Password
        </label>
        <div className="mt-1 relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Lock className="h-5 w-5 text-gray-400" />
          </div>
          <input
            id="password_confirm"
            name="password_confirm"
            type={showConfirmPassword ? 'text' : 'password'}
            autoComplete="new-password"
            required
            value={formData.password_confirm}
            onChange={handleInputChange}
            className={`block w-full pl-10 pr-10 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 sm:text-sm ${
              errors.password_confirm ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="Confirm your password"
          />
          <button
            type="button"
            className="absolute inset-y-0 right-0 pr-3 flex items-center"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
          >
            {showConfirmPassword ? (
              <EyeOff className="h-5 w-5 text-gray-400" />
            ) : (
              <Eye className="h-5 w-5 text-gray-400" />
            )}
          </button>
        </div>
        {renderFieldError('password_confirm')}
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-4">
      <div>
        <label htmlFor="business_name" className="block text-sm font-medium text-gray-700">
          Business Name
        </label>
        <div className="mt-1 relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Building className="h-5 w-5 text-gray-400" />
          </div>
          <input
            id="business_name"
            name="business_name"
            type="text"
            required
            value={formData.business_name}
            onChange={handleInputChange}
            className={`block w-full pl-10 pr-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 sm:text-sm ${
              errors.business_name ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="Enter your business name"
          />
        </div>
        {renderFieldError('business_name')}
      </div>

      <div>
        <label htmlFor="business_type" className="block text-sm font-medium text-gray-700">
          Business Type
        </label>
        <div className="mt-1">
          <select
            id="business_type"
            name="business_type"
            value={formData.business_type}
            onChange={handleInputChange}
            className={`block w-full py-2 px-3 border bg-white rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 sm:text-sm ${
              errors.business_type ? 'border-red-300' : 'border-gray-300'
            }`}
          >
            <option value="individual">Individual</option>
            <option value="company">Company</option>
            <option value="partnership">Partnership</option>
          </select>
        </div>
        {renderFieldError('business_type')}
      </div>

      <div>
        <label htmlFor="experience_years" className="block text-sm font-medium text-gray-700">
          Years of Experience
        </label>
        <div className="mt-1">
          <input
            id="experience_years"
            name="experience_years"
            type="number"
            min="0"
            max="50"
            value={formData.experience_years}
            onChange={handleInputChange}
            className="block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 sm:text-sm"
            placeholder="Years of experience"
          />
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex items-center">
          <input
            id="terms_accepted"
            name="terms_accepted"
            type="checkbox"
            checked={formData.terms_accepted}
            onChange={handleInputChange}
            className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
            required
          />
          <label htmlFor="terms_accepted" className="ml-2 block text-sm text-gray-700">
            I accept the{' '}
            <Link to="/terms" className="text-green-600 hover:text-green-500">
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link to="/privacy" className="text-green-600 hover:text-green-500">
              Privacy Policy
            </Link>
          </label>
        </div>
        {renderFieldError('terms_accepted')}

        <div className="flex items-center">
          <input
            id="background_check_consent"
            name="background_check_consent"
            type="checkbox"
            checked={formData.background_check_consent}
            onChange={handleInputChange}
            className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
            required
          />
          <label htmlFor="background_check_consent" className="ml-2 block text-sm text-gray-700">
            I consent to background verification checks
          </label>
        </div>
        {renderFieldError('background_check_consent')}

        <div className="flex items-center">
          <input
            id="marketing_consent"
            name="marketing_consent"
            type="checkbox"
            checked={formData.marketing_consent}
            onChange={handleInputChange}
            className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
          />
          <label htmlFor="marketing_consent" className="ml-2 block text-sm text-gray-700">
            I would like to receive marketing communications and business tips
          </label>
        </div>
      </div>
    </div>
  );

  return (
    <div className={`min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8 ${className}`}>
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-green-100">
            <Shield className="h-6 w-6 text-green-600" />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Become a Partner
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Join our network of trusted service providers
          </p>
        </div>

        {/* Step indicator */}
        <div className="flex items-center justify-center space-x-4">
          <div className={`flex items-center ${currentStep >= 1 ? 'text-green-600' : 'text-gray-400'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
              currentStep >= 1 ? 'border-green-600 bg-green-600 text-white' : 'border-gray-300'
            }`}>
              1
            </div>
            <span className="ml-2 text-sm font-medium">Personal</span>
          </div>
          <div className={`flex-1 h-px ${
            currentStep >= 2 ? 'bg-green-600 w-full' : 'bg-gray-200 w-0'
          }`} />
          <div className={`flex items-center ${currentStep >= 2 ? 'text-green-600' : 'text-gray-400'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
              currentStep >= 2 ? 'border-green-600 bg-green-600 text-white' : 'border-gray-300'
            }`}>
              2
            </div>
            <span className="ml-2 text-sm font-medium">Business</span>
          </div>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {errors.general && (
            <div className="rounded-md bg-red-50 border border-red-200 p-4">
              <div className="flex">
                <AlertCircle className="h-5 w-5 text-red-400" />
                <div className="ml-3">
                  <p className="text-sm text-red-800">{errors.general}</p>
                </div>
              </div>
            </div>
          )}

          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}

          <div className="flex space-x-4">
            {currentStep > 1 && (
              <button
                type="button"
                onClick={handlePrevStep}
                className="flex-1 py-3 px-4 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors duration-200"
              >
                Previous
              </button>
            )}
            
            <button
              type="submit"
              disabled={isLoading || (currentStep === 2 && (!formData.terms_accepted || !formData.background_check_consent))}
              className="flex-1 group relative flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              {isLoading ? (
                <>
                  <Loader2 className="animate-spin -ml-1 mr-3 h-5 w-5" />
                  Creating Account...
                </>
              ) : currentStep < 2 ? (
                'Next Step'
              ) : (
                'Create Partner Account'
              )}
            </button>
          </div>

          <div className="text-center">
            <p className="text-sm text-gray-600">
              Already have an account?{' '}
              <Link
                to="/login"
                className="font-medium text-green-600 hover:text-green-500 transition-colors duration-200"
              >
                Sign in here
              </Link>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PartnerRegisterForm;